import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { ICardMetadata, ClassMetadata, IMetadataChipAction, DocumentSearchCriteria, IHeaderBadge, } from "@tylertech/tyler-api-types";
import { OmniBarService } from "@tylertech/core-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { MetadataService, MetadataLoadViewService, CodeLookupService } from "../../services";
import { MetadataIntermediary, RepositoryFormData } from "../../interfaces";
import { DocumentService } from "@tylertech/attachments-module";
import { ActionCallBackArgs } from "../metadataForm/metadataAction.component";
import { ForgeCardModule, ForgeAvatarModule, ForgeBadgeModule, ForgeIconButtonModule, ForgeTooltipModule, ForgeIconModule } from "@tylertech/forge-angular";
import { LayoutDirective, LayoutAlignDirective, FlexDirective } from "@ngbracket/ngx-layout/flex";
import { NgClass } from "@angular/common";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { MetadataActionMenuComponent } from "../metadataForm/metadataActionMenu.component";
import { MetadataPanelComponent } from "../metadataPanel/metadataPanel.component";

@Component({
    selector: "metadata-card-view",
    templateUrl: "./metadataCard.view.tmpl.html",
    imports: [
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeAvatarModule,
        ForgeBadgeModule,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        MetadataActionMenuComponent,
        MetadataPanelComponent,
        NgClass,
    ]
})
export class MetadataCardViewComponent implements OnInit
{
    protected $omniBarService = inject(OmniBarService);
    private $codeLookupService = inject(CodeLookupService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private $documentApiService = inject(DocumentService);
    private readonly $metadataLoadViewService = inject(MetadataLoadViewService);
    private $metadataService = inject(MetadataService);

    @Input()
    cardMetadata: ICardMetadata;

    @Input()
    model: any;

    @Input()
    datacontroller: RepositoryFormData;

    @Input()
    dense: boolean;

    @Output()
    readonly toggleEdit = new EventEmitter<void>();

    displayModel: any;

    metadata: ClassMetadata;

    emblemText: string;

    subHeaders: string[] = [];

    onClicked(): void
    {
        this.toggleEdit.emit();
    }

    ngOnInit(): void
    {
        this.loadCardView();
    }

    loadCardView(): void
    {
        const self = this;
        this.emblemText = this.cardMetadata.EmblemText
            ? this.$metadataService.EvaluateString(this.cardMetadata.EmblemText, null, this.model)
            : null;
        this.setHeaderElements();
        this.displayModel = Utilities.deepCopy(this.model);
        this.metadata = Utilities.deepCopy(this.cardMetadata.ViewMetaData);
        const properties = this.$metadataService.FlattenProperties(this.metadata, this.model.$$Parent, this.model);
        //for pre-existing usages that do not set up the widths, give it something readable
        const propsWithWidth = properties.filter(x => x.property.DrawWidth !== 100);
        const autoSizeProperties = properties.length > 1 && propsWithWidth.length === 0;
        properties.forEach(p =>
        {
            this.getValue(p)
                .then(v =>
                {
                    self.displayModel[p.name] = v;
                    if (p.property.InputType === "Check")
                    {
                        p.property["$InputType"] = "Check";
                    }
                    if (p.property.InputType !== "Link" && p.property.InputType !== "Badge")
                    {
                        p.property.InputType = "LabelValue";
                    }
                    if (autoSizeProperties)
                    {
                        p.property.DrawWidth = 25;
                    }
                });
        });
        this.initializeChipActions();
    }

    getValue(intermediary: MetadataIntermediary): Promise<string>
    {
        const value = intermediary.model[intermediary.name];
        if (Utilities.IsNullOrUndefined(value)) return Promise.resolve("");
        if (intermediary.property.Lookup)
        {
            let fieldValue = "";
            const builder = this.$odataFilterFactory.createBuilder();
            builder.filterEQ("Key", value);
            return this.$codeLookupService.Get(intermediary.property.Lookup, builder.build())
                .then(
                    x =>
                    {
                        if (x && x.length > 0) fieldValue = x[0].Value;
                        return fieldValue;
                    },
                    () =>
                    {
                        return "Error Loading";
                    }
                );
        }
        return Promise.resolve(this.$metadataService.getFormattedValue(intermediary));
    }

    hasEmblemText(): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.emblemText);
    }

    get title(): string
    {
        return this.$metadataService.EvaluateString(this.cardMetadata.Title, this.model.$$Parent, this.model);
    }

    setHeaderElements(): void
    {
        const self = this;

        if (this.cardMetadata.HeaderBadges)
        {
            this.headerBadges = [];
            this.cardMetadata.HeaderBadges.forEach(badge =>
            {
                const text = this.$metadataService.EvaluateString(
                    badge.Text, null, this.model
                );

                this.headerBadges.push({
                    Theme: badge.Theme,
                    Text: text,
                    HiddenProperty: badge.HiddenProperty
                });
            });
        }
        else
        {
            this.headerBadges = null;
        }

        if (!this.cardMetadata.SubtitleMetaData) return;
        const properties = this.$metadataService
            .FlattenProperties(this.cardMetadata.SubtitleMetaData, this.model.$$Parent, this.model)
            .filter(
                x =>
                    !this.$metadataService.IsHiddenProperty(x.property) &&
                    !this.$metadataService.EvaluateBoolean(x.property.HiddenProperty, x.parent, x.model)
            )
            .sort((a, b) => a.property.RenderPosition - b.property.RenderPosition);
        properties.forEach(p =>
        {
            const value = p.model[p.name];
            if (!Utilities.IsNullOrUndefined(value))
            {
                if (p.property.Lookup)
                {
                    let fieldValue = "";
                    const builder = this.$odataFilterFactory.createBuilder();
                    builder.filterEQ("Key", value);
                    this.$codeLookupService.Get(p.property.Lookup, builder.build())
                        .then(x =>
                        {
                            if (x && x.length > 0) fieldValue = x[0].Value;
                            self.subHeaders.push(fieldValue);
                        });
                }
                else self.subHeaders.push(this.$metadataService.getFormattedValue(p));
            }
        });
    }

    initializeChipActions(): void
    {
        if (!this.cardMetadata.ChipMetadata) return;
        this.cardMetadata.ChipMetadata.forEach(action =>
        {
            action["$OriginalLabel"] = action.Label;
            this.loadChipCount(action);
        });
    }

    loadChipCount(chipAction: IMetadataChipAction, forceReload?: boolean): void
    {
        const self = this;
        const REPOSITORY_COLLECTION_PROPERTY_PATH = "$Loaded" + chipAction.Label;
        if (chipAction["$OriginalLabel"].indexOf(" $count$") < 0 && !forceReload)
            //uses model property to evaluate, dont need separate call
            return;
        if (
            !Utilities.IsNullOrUndefined(chipAction.ViewToShow["IsTCMAttachmentView"]) &&
            chipAction.ViewToShow["IsTCMAttachmentView"]
        )
        {
            const criteria = <DocumentSearchCriteria>{
                EntityId: this.model[chipAction.ViewToShow.ParentColumnName],
                EntityType: chipAction.ViewToShow.ViewMetaData.TypeName,
            };
            this.$documentApiService.GetDocumentCount(criteria)
                .then(
                    result =>
                    {
                        const count: number = result.Result;
                        if (!forceReload)
                        {
                            chipAction.Label = chipAction["$OriginalLabel"].replace("$count$", "(" + count + ")");
                        }
                        else { chipAction.Label = chipAction.Label.replace(/\(\d+\)/, "(" + count + ")"); }

                    },
                    _err =>
                    {
                        if (!forceReload)
                        {
                            chipAction.Label = chipAction["$OriginalLabel"].replace("$count$", "(0)");
                        }
                        else { chipAction.Label = chipAction.Label.replace(/\(\d+\)/, "(0)"); }

                    }
                );
        }
        else
        {
            const data =
                !forceReload && this.cardMetadata[REPOSITORY_COLLECTION_PROPERTY_PATH]
                    ? this.cardMetadata[REPOSITORY_COLLECTION_PROPERTY_PATH]
                    : new RepositoryFormData(null, null);
            this.$metadataLoadViewService.LoadView(this.model, chipAction.ViewToShow, data)
                .then(
                    x =>
                    {
                        self.cardMetadata[REPOSITORY_COLLECTION_PROPERTY_PATH] = data;
                        const count = x ? x.length : 0;
                        if (!forceReload)
                        {
                            chipAction.Label = chipAction["$OriginalLabel"].replace("$count$", "(" + count + ")");
                        }
                        else { chipAction.Label = chipAction.Label.replace(/\(\d+\)/, "(" + count + ")"); }
                    },
                    _err =>
                    {
                        if (!forceReload)
                        {
                            chipAction.Label = chipAction["$OriginalLabel"].replace("$count$", "(0)");
                        }
                        else { chipAction.Label = chipAction.Label.replace(/\(\d+\)/, "(0)"); }
                    }
                );
        }
    }

    afterAction(callback: ActionCallBackArgs): void
    {
        this.loadChipCount(<IMetadataChipAction>callback.action, true);
    }

    get canEdit(): boolean
    {
        if (!this.cardMetadata.CanEdit) return false;
        if (!Utilities.IsNullOrUndefined(this.cardMetadata.CanEditProperty))
            return this.$metadataService.EvaluateBoolean(
                this.cardMetadata.CanEditProperty,
                this.model["$$Parent"],
                this.model
            );
        return this.cardMetadata.CanEdit;
    }

    get showHeaderBadge(): boolean
    {
        return !!this.visibleHeaderBadges && this.visibleHeaderBadges.length > 0;
    }

    private _headerBadges: IHeaderBadge[] = null;
    get headerBadges(): IHeaderBadge[]
    {
        return this._headerBadges;
    }

    set headerBadges(value: IHeaderBadge[])
    {
        this._headerBadges = value;
    }

    get visibleHeaderBadges(): IHeaderBadge[]
    {
        return this._headerBadges?.filter(
            x => !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model) && x.Text.length > 0
        );
    }

    protected readonly onclick = onclick;
}
